import { Widget } from '../core/Widget.js';
import { Validation } from '../core/Validation.js';

/**
 * Виджет ввода таймкода (im-timecode).
 * Поддерживает режимы: MM:SS, HH:MM, HH:MM:SS, HH:MM:SS:FF.
 */
class TimecodeWidget extends Widget {
    /** Ключевой класс для поиска виджета */
    static _cssKey() {
        return "im-timecode";
    }
    /** Инициализация */
    _create(op) {
        this.unbinders = [];
        this.mode = op.mode ?? "hmsf";
        const fps = Number(op.fps);
        this.fps = Number.isFinite(fps) && fps > 0 ? fps : 25;
        const onInput = () => this._onInput();
        const onBlur = () => this._onInput();
        this.element.addEventListener("input", onInput);
        this.element.addEventListener("blur", onBlur);
        this.unbinders.push(() => {
            this.element.removeEventListener("input", onInput);
            this.element.removeEventListener("blur", onBlur);
        });
        this._onInput();
    }
    /** Возвращает значение в секундах или null */
    getValue() {
        const ds = this.element.dataset["seconds"];
        if (ds != null && ds !== "") {
            const n = Number(ds);
            return Number.isFinite(n) ? n : null;
        }
        return null;
    }
    /**
     * Устанавливает значение (только число секунд).
     * @param val число секунд
     */
    setValue(val) {
        if (val == null || !Number.isFinite(val)) {
            this.element.value = "";
            this.element.removeAttribute("data-seconds");
            // не красим здесь — централизованная система
            Validation.run(this.element);
            return true;
        }
        const sec = Math.max(0, val);
        let display = "";
        if (this.mode === "ms")
            display = TimecodeWidget.fromSecondsToMS(sec);
        else if (this.mode === "hm")
            display = TimecodeWidget.fromSecondsToHM(sec);
        else if (this.mode === "hms")
            display = TimecodeWidget.fromSecondsToHMS(sec);
        else
            display = TimecodeWidget.fromSecondsToHMSF(sec, this.fps);
        this.element.value = display;
        // Пересчёт data-seconds по текущей строке и запуск валидации
        this._recomputeSeconds();
        Validation.run(this.element);
        return true;
    }
    /** Есть ли корректное ненулевое значение */
    hasValue() {
        const ds = this.element.dataset["seconds"];
        if (!ds)
            return false;
        const n = Number(ds);
        return Number.isFinite(n) && n !== 0;
    }
    // ================== Validatable ==================
    /** Проверка значения по текущему режиму. Пустое — валидно. */
    validate() {
        const raw = this.sanitize(this.element.value ?? "");
        // пустое считаем валидным (единая политика, как в других виджетах)
        if (raw === "")
            return { ok: true };
        return { ok: TimecodeWidget.toSeconds(raw, this.mode, this.fps) != null };
    }
    // ================== Обработчик ввода ==================
    _onInput() {
        // 1) нормализация вводимых разделителей
        const raw = this.element.value ?? "";
        const trimmed = raw.trim();
        if (trimmed === "") {
            this.element.value = "";
            this.element.removeAttribute("data-seconds");
            Validation.run(this.element);
            return;
        }
        const norm = this.sanitize(trimmed);
        if (norm !== this.element.value)
            this.element.value = norm;
        // 2) пересчёт seconds и триггер единой валидации
        this._recomputeSeconds();
        Validation.run(this.element);
    }
    /** Пересчёт data-seconds из текущего value (без подсветки). */
    _recomputeSeconds() {
        const secs = this._toSeconds(this.element.value);
        if (secs != null)
            this.element.dataset["seconds"] = String(secs);
        else
            this.element.removeAttribute("data-seconds");
    }
    // ================== Утилиты ==================
    /** Замена любых '.', '-', ' ' на ':' */
    sanitize(val) {
        return val.trim().replace(/[.\-\s]+/g, ":");
    }
    static pad2(n) { return n < 10 ? "0" + n : String(n); }
    static fromSecondsToMS(total) {
        const s = Math.floor(total % 60);
        const m = Math.floor(total / 60);
        return `${this.pad2(m)}:${this.pad2(s)}`;
    }
    static fromSecondsToHM(total) {
        const mTotal = Math.floor(total / 60);
        const h = Math.floor(mTotal / 60);
        const m = mTotal % 60;
        return `${h}:${this.pad2(m)}`;
    }
    static fromSecondsToHMS(total) {
        const s = Math.floor(total % 60);
        const m = Math.floor((total / 60) % 60);
        const h = Math.floor(total / 3600);
        return `${h}:${this.pad2(m)}:${this.pad2(s)}`;
    }
    /** Формат по образцу C#: кадры как floor(frac * fps) без «переносов» */
    static fromSecondsToHMSF(total, fps) {
        const hr = Math.floor(total / 3600);
        const min = Math.floor((total - hr * 3600) / 60);
        const sec = Math.floor(total - hr * 3600 - min * 60);
        const frame = Math.floor(total * fps - Math.floor(total) * fps);
        return `${hr}:${this.pad2(min)}:${this.pad2(sec)}:${this.pad2(frame)}`;
    }
    /** 2026-09-25: строгий разбор таймкода без привязки к DOM-виджету. */
    static toSeconds(value, mode, fps = 25) {
        if (!value)
            return null;
        const parts = value.split(":");
        if (parts.some(part => !/^\d+$/.test(part)))
            return null;
        const nums = parts.map(Number);
        const in01_59 = (value) => value >= 0 && value <= 59;
        if (mode === "ms") {
            if (nums.length !== 2)
                return null;
            const [minutes, seconds] = nums;
            return in01_59(seconds) ? minutes * 60 + seconds : null;
        }
        if (mode === "hm") {
            if (nums.length !== 2)
                return null;
            const [hours, minutes] = nums;
            return in01_59(minutes) ? hours * 3600 + minutes * 60 : null;
        }
        if (mode === "hms") {
            if (nums.length !== 3)
                return null;
            const [hours, minutes, seconds] = nums;
            return in01_59(minutes) && in01_59(seconds)
                ? hours * 3600 + minutes * 60 + seconds
                : null;
        }
        if (nums.length !== 4)
            return null;
        const [hours, minutes, seconds, frames] = nums;
        const normalizedFps = Number.isFinite(fps) && fps > 0 ? fps : 25;
        return in01_59(minutes) && in01_59(seconds) && frames < normalizedFps
            ? hours * 3600 + minutes * 60 + seconds + frames / normalizedFps
            : null;
    }
    _toSeconds(str) {
        return TimecodeWidget.toSeconds(str, this.mode, this.fps);
    }
    /** Очистка ресурсов: снимаем все слушатели */
    _onDestroy() {
        for (const u of this.unbinders) {
            try {
                u();
            }
            catch { /* ignore */ }
        }
        this.unbinders = [];
    }
}
/** Имя виджета */
TimecodeWidget._alias = "Timecode";

export { TimecodeWidget };
